/**
 * DemoShowcaseTree — MOCKUP of the /tutorials catalog reframed around the
 * 2026-09-03 bring-your-own-strategy pivot (see docs/CUSTOM_AUTOMATION_SERVICE.md
 * and docs/DEMO_STRATEGY_VIDEO_PLAN.md). Most videoSrc entries below still point
 * at a shared placeholder clip (tutorials/videos/demo_showcase/placeholder.mp4) —
 * "00" (Welcome), "P02" (Why Manual Options Management Fails), "T01"
 * (Trust & Control), "NEW-BYOS" (Bring Your Own Strategy), everything in the
 * "Platform Features" sub-section except NEW-BACKTEST/NEW-AIBOT/NEW-ADMIN/
 * NEW-TICKETS, "03" (Understanding the Rule Engine), and the Pyramid Hedging
 * items C00/PH-S plus every item under the Setup 1 / Setup 2 Trading Setups
 * children are the exceptions produced so far (see the VIDEO_SRC constants
 * above). P02, T01, 03, C00, PH-S, and all Platform Features/Setup 1/2 items
 * with real footage reuse the real /tutorials clips — copied in unchanged
 * 2026-09-04 (see tutorials/videos/platform/ and
 * tutorials/videos/pyramid_hedging/ for the originals). Per explicit request, T01's demo_showcase copy also got a
 * one-word narration patch at ~1:35 ("your clients' capital" → "your
 * capital", via remotion/splice_audio.py); the real /tutorials copy was
 * deliberately left unchanged. NEW-BYOS is a net-new Remotion render
 * (record_id byos_1788510250405, 2026-09-04) built from
 * 05_bring_your_own_strategy.md's narration, reusing the Welcome video's
 * suggestions-page screenshot asset for its approval-gate beat. Pyramid
 * Hedging's Comparisons track (C01–C05, real-catalog "switch to PH" pitches)
 * was cut entirely the same day — see docs/DEMO_STRATEGY_VIDEO_PLAN.md.
 * This is a standalone copy of TutorialTree.jsx, not a shared/data-driven
 * refactor of it, so the real /tutorials page is completely unaffected by anything
 * in this file.
 *
 * Purpose: give a real in-app visual of how the video library looks once
 * Pyramid Hedging (PH) and Weekly Index Short Straddle (WISS) are reframed
 * as capability showcases rather than sold strategies, Futures Pyramid
 * Hedging (FPH) is dropped from the showcase set, and new
 * repositioning/bring-your-own-strategy content is added. See
 * docs/DEMO_STRATEGY_VIDEO_PLAN.md for the reasoning behind every change
 * here relative to TutorialTree.jsx.
 */

import React, { useRef, useEffect, useState } from "react";

const PLACEHOLDER = "/tutorials/demo_showcase/placeholder.mp4";
const PLATFORM_BASICS_PLACEHOLDER = "/tutorials/demo_showcase/platform_basics/placeholder.mp4";
const PB_SCRIPT_DIR = "tutorials/scripts/demo_showcase/platform_basics/";

// "00 — Welcome" produced 2026-09-04 (Remotion animation + one screenshot_zoom
// beat over the live Trade Suggestions/approval-queue page, Robert narration).
// Every other item below still points at PLATFORM_BASICS_PLACEHOLDER.
const WELCOME_VIDEO_SRC = "/tutorials/demo_showcase/platform_basics/00-welcome.mp4";

// "P02 — Why Manual Options Management Fails" filled in 2026-09-04 with the
// already-produced real /tutorials clip (tutorials/videos/platform/p02-manual-options.mp4,
// copied alongside it here) rather than a new demo_showcase-specific recording.
const MANUAL_OPTIONS_VIDEO_SRC = "/tutorials/demo_showcase/platform_basics/p02-manual-options.mp4";

// "T01 — Trust & Control" filled in 2026-09-04, same as P02 above (real
// /tutorials clip copied in, not a new recording) — plus two narration
// patches applied only to this copy via remotion/splice_audio.py against the
// original narration source (remotion audio dir: t01_narration.mp3):
// "your clients' credentials" → "your credentials" (~0:39) and
// "your clients' capital" → "your capital" (~1:35). Both were already
// correct in this file's own script (03_trust_and_control.md) — only the
// rendered audio was stale.
const TRUST_CONTROL_VIDEO_SRC = "/tutorials/demo_showcase/platform_basics/t01-approval.mp4";

// "NEW-BYOS — Bring Your Own Strategy" produced 2026-09-04: a net-new
// Remotion animation (title/flow-diagram/screenshot_zoom/CTA scenes) built
// from 05_bring_your_own_strategy.md's narration, Robert voice via ElevenLabs.
// Closes with the WISS-then-Pyramid-Hedging teaser bridging into the next section.
const BYOS_VIDEO_SRC = "/tutorials/demo_showcase/platform_basics/05-bring-your-own-strategy.mp4";

// "01 — Dashboard Walkthrough" and "03 — Understanding the Rule Engine"
// filled in 2026-09-04 with the real /tutorials clips (platform/01-dashboard.mp4,
// platform/03-rules.mp4), copied in unchanged — same pattern as P02/T01 above.
const DASHBOARD_VIDEO_SRC = "/tutorials/demo_showcase/platform_basics/06-dashboard.mp4";
const RULE_ENGINE_VIDEO_SRC = "/tutorials/demo_showcase/platform_basics/07-rules.mp4";

// "Platform Features" section added 2026-09-04 as a nested sub-section of
// Platform Basics, then promoted to its own top-level section the same day —
// a scannable tree of small, single-feature clips (Dashboard moved in here
// from its old top-level slot) so the viewer can see every feature at a
// glance without one long video. Most items below reuse real /tutorials
// footage, copied in unchanged; NEW-POSITIONS, NEW-BACKTEST, NEW-AIBOT,
// NEW-ADMIN, and NEW-TICKETS have no existing footage anywhere and are
// script-only for now (see their .md files in
// tutorials/scripts/demo_showcase/platform_basics/). NEW-POSITIONS replaced
// an earlier "Monitoring Positions & Greeks" item (removed 2026-09-04) that
// pointed at the strategy-specific option-Greeks table — not representative
// of the generic /trading/positions page every customer actually sees.
// "NEW-POSITIONS — Portfolio Positions" produced 2026-09-05: a net-new
// record-job capture of the real /trading/positions page (PortfolioDashboard.jsx)
// on the neha_chopra demo account — balance strip, P&L strip, Columns picker
// (toggling the two delta columns on), a native HTML5 column drag-reorder, and
// the New Trade modal. Robert narration via ElevenLabs, no intro/outro bumper
// (brand-neutral body, matching the rest of this series). Required a runner.mjs
// fix: `remotion/runner.mjs`'s `handlers.drag` (mouse-simulated
// page.dragAndDrop() didn't trigger this component's native dragstart/dragenter/
// dragend handlers reliably under headless Chromium — replaced with direct
// DragEvent dispatch via elementHandle) — and the drag step had to run as the
// first interactive action after page load: any prior state-changing click
// (e.g. opening the Columns picker) left the synthetic dispatch unable to
// reorder columns on a subsequent drag, even though the same drag succeeded
// reliably right after page load. Real cause not fully root-caused; reordering
// the video's own step sequence (drag before the Columns-picker beat) was the
// pragmatic fix, not a change to app code.
const PORTFOLIO_POSITIONS_VIDEO_SRC = "/tutorials/demo_showcase/platform_basics/portfolio-positions.mp4";
// "NEW-AIBOT — The AI Assistant" produced 2026-09-05: a net-new record-job
// capture of the real /chat page (AIChatPage.jsx) on the neha_chopra demo
// account — two real, live-tested questions (one account-scoped via the
// tool-using agent, one generic platform-mechanics question), the "+ New
// Chat" reset, and a cutaway to the real /automation/suggestions approval
// queue to reinforce the "explains, doesn't execute" boundary. Robert
// narration, no intro/outro bumper. The script's original storyline
// described a session-switching sidebar that doesn't exist in the real
// component (ChatContext.js merges history into one continuous thread with
// a single New Chat reset, no sidebar) — narration's [00:27] line was
// rewritten to match actual behavior before recording.
// Also required a real infra fix along the way: docker-compose.yml's
// backend volumes had `./docs:/app/docs:ro` shadowing `backend/docs/`
// (kb/ + chatbot_kb.md) at the same /app/docs path, which had silently
// broken the whole chat feature — the KB-only path fell back to "No
// knowledge base available." (confidence 0, escalates every message) and
// the account-scoped agent path 500'd outright (empty agent_policy.md ->
// Claude API rejects an empty system content block). Fixed by removing
// that mount (its one consumer, gateway_routes.py's PEG_API_REFERENCE.md,
// had already moved to docs/archive/ during the 2026-09-03 pivot anyway).
const AI_ASSISTANT_VIDEO_SRC = "/tutorials/demo_showcase/platform_basics/ai-assistant.mp4";
// "NEW-TICKETS — Support & Tickets" produced 2026-09-05: screenshot_zoom capture
// of the real /support/tickets form (SupportTicketsPage.jsx) on the anshu demo
// account. chatwootWebsiteToken is blank on this deployment (partners/growfin/
// app-config.js), which hides the form entirely behind a "not configured yet"
// placeholder — temporarily set to a placeholder value only long enough to
// screenshot the empty and filled states (no submission, no live Chatwoot API
// call), then reverted. Success-screen state skipped per the script's own
// production notes — narration never depends on it.
const SUPPORT_TICKETS_VIDEO_SRC = "/tutorials/demo_showcase/platform_basics/support-tickets.mp4";
// "WISS-I — Introduction" produced 2026-09-05: screenshot_zoom capture, same
// production as the real /tutorials copy — see the matching comment on
// TutorialTree.jsx's WISS-I entry for the full production notes (data seeded
// directly since no working Tradier token exists on this deployment; found
// but did not fix a Strategy Manage dead-code bug for WISS along the way).
const WISS_INTRO_VIDEO_SRC = "/tutorials/demo_showcase/wiss/wiss_intro.mp4";
// "NEW-BACKTEST — Backtesting Overview" produced 2026-09-05: a net-new
// record-job capture of the real /backtest and /backtest/replay/:jobId pages
// on the neha_chopra demo account — filling the config form (MSFT, matching
// BT02's real date range for continuity, not submitted), toggling the
// Interactive/Auto-Run tabs (both showing real existing sessions for this
// account), then jumping to the end of a real completed Auto-Run job (CRM,
// 2026-07-24 to 2026-08-21, 21 days / 22 real adjustments) via its ⏭
// skip-to-end control to show the real "Backtest Complete" summary and
// Adjustments Log — nothing scripted or mocked. Robert narration, no
// intro/outro bumper.
const BACKTEST_OVERVIEW_VIDEO_SRC = "/tutorials/demo_showcase/platform_basics/backtesting-overview.mp4";
// Cache-busted (2026-09-05 re-render) — see the matching comment on
// TutorialTree.jsx's videoSrc for "07" for why this is needed.
const APPROVAL_FLOW_VIDEO_SRC = "/tutorials/demo_showcase/platform_basics/approval-flow.mp4?v=1788591600";
const PRICE_ALERTS_VIDEO_SRC = "/tutorials/demo_showcase/platform_basics/price-alerts.mp4";
const NOTIFICATION_SETTINGS_VIDEO_SRC = "/tutorials/demo_showcase/platform_basics/notification-settings.mp4";
const RULE_TEMPLATES_VIDEO_SRC = "/tutorials/demo_showcase/platform_basics/rule-templates.mp4";
const BROKERAGE_CONNECT_VIDEO_SRC = "/tutorials/demo_showcase/platform_basics/brokerage-connect.mp4";
const SYSTEM_SETTINGS_VIDEO_SRC = "/tutorials/demo_showcase/platform_basics/system-settings.mp4";

// Pyramid Hedging Strategy Guide + Trading Setups videos filled in 2026-09-04,
// all real /tutorials/pyramid_hedging/ clips copied in unchanged (no re-edits,
// unlike T01). Only items with actual produced footage were copied — the
// "Rules to Add" and not-yet-recorded backtest slots (TS1-BT-TREND,
// TS1-BT-FLAT, TS2-RULES, TS2-BT-TREND, TS2-BT-FALL, TS2-BT-FLAT in the real
// catalog) are left out of this mockup rather than added as new placeholders.
const PH_SWISS_ARMY_VIDEO_SRC = "/tutorials/demo_showcase/pyramid_hedging/c00-swiss-army.mp4";
const PH_SETTINGS_GUIDE_VIDEO_SRC = "/tutorials/demo_showcase/pyramid_hedging/ph_settings_guide.mp4";
const PH_TE01_VIDEO_SRC = "/tutorials/demo_showcase/pyramid_hedging/te01-csp-disabled.mp4";
const PH_TE03_VIDEO_SRC = "/tutorials/demo_showcase/pyramid_hedging/te03-income-first-mode-off.mp4";
const PH_TE04_VIDEO_SRC = "/tutorials/demo_showcase/pyramid_hedging/te04-income-first-mode-on.mp4";
const PH_BT02_VIDEO_SRC = "/tutorials/demo_showcase/pyramid_hedging/bt02_msft_one_month_backtest.mp4";
const PH_TE02_VIDEO_SRC = "/tutorials/demo_showcase/pyramid_hedging/te02-csp-enabled.mp4";
const PH_C06_VIDEO_SRC = "/tutorials/demo_showcase/pyramid_hedging/c06-income-first-mode.mp4";

/* ── Mockup data — reframed platform basics + two showcase strategies + a
   closing "build yours" pitch ─────────────────────────────────────────── */
export const SHOWCASE_TUTORIALS = [
  /* ─────────────────────────────────────────────────────────────────────
   * 1 — PLATFORM BASICS (generic, plus the new repositioning video).
   * Order fixed 2026-09-03, trimmed 2026-09-04: 00 → P02 → T01 → NEW-BYOS →
   * 03. P01 ("The Multi-Account Problem") and NEW-REPO ("This Is a
   * Demo Strategy") were cut for conciseness — see
   * docs/DEMO_STRATEGY_VIDEO_PLAN.md's 2026-09-04 note. NEW-REPO's "PH/WISS
   * are demos, not products" framing is still carried by NEW-CUSTOM-CTA at
   * the end of the catalog; its "here's what's coming" role is now carried by
   * new closing lines in NEW-BYOS's own script (teasing WISS, then PH).
   * "Platform Features" (Dashboard Walkthrough and every other operational
   * feature) was briefly nested here, then promoted to its own top-level
   * section the same day per explicit request — see section 2 below.
   * Scripts for every item below: tutorials/scripts/demo_showcase/platform_basics/.
   * ───────────────────────────────────────────────────────────────────── */
  {
    series: "Platform Basics",
    color: "#545cd8",
    icon: "📚",
    description: "Start here. What this platform actually sells, why manual options management doesn't scale, and how the platform stays in your control.",
    items: [
      {
        id: "00", title: "Welcome", duration: "~55s", priority: "P0",
        desc: "This platform runs your strategy — not ours. Pyramid Hedging and Weekly Index Short Straddle are shown here purely as demonstrations of what a fully engineered strategy looks like on this rule engine. Study them, then bring your own, built from your own rules.",
        topics: [
          "What gets automated: your own rule-based strategy, on your own broker connection",
          "The rule engine cycle: how it evaluates rules every 5 minutes and queues suggestions",
          "Human-in-the-loop: every suggestion requires your approval before any order executes",
        ],
        videoSrc: WELCOME_VIDEO_SRC,
        script: PB_SCRIPT_DIR + "00_welcome.md",
      },
      {
        id: "P02", title: "Why Manual Options Management Fails", duration: "90s", priority: "P0",
        desc: "Even for a single account, manual options management has a structural inconsistency problem. Here's the evidence.",
        topics: [
          "Decision fatigue at expiry — why the last week is the hardest",
          "Inconsistent roll timing and what it costs over 12 months",
          "The compounding cost of reactive management vs systematic rules",
        ],
        videoSrc: MANUAL_OPTIONS_VIDEO_SRC,
        script: PB_SCRIPT_DIR + "02_why_manual_options_management_fails.md",
      },
      {
        id: "T01", title: "Trust & Control", duration: "60s", priority: "P0",
        desc: "The system never places an order without your explicit approval. You stay in full control at every step, no matter whose strategy is running underneath.",
        topics: [
          "Human-in-the-loop design: why every suggestion requires explicit approval before any order is placed",
          "How to approve, reject, or snooze any suggestion — and what each choice means",
          "Full audit trail: every decision, every order, every outcome recorded permanently",
        ],
        videoSrc: TRUST_CONTROL_VIDEO_SRC,
        script: PB_SCRIPT_DIR + "03_trust_and_control.md",
      },
      {
        id: "NEW-BYOS", title: "Bring Your Own Strategy", duration: "~2 min", priority: "P0",
        desc: "How a strategy you already run — manual, spreadsheet-driven, or built in a retail bot tool — gets translated into this platform's rule engine, staged for your approval, and executed on your own infrastructure.",
        topics: [
          "From your rules to the platform's condition-tree language (facts, conditions, actions)",
          "Every suggestion sits in the approval queue — nothing executes without you clicking approve",
          "Setup fee (translate + deploy your strategy) + monthly platform/support fee — no AUM or performance cut",
          "What's coming next: Weekly Index Short Straddle (simple concept, hard to run by hand) and Pyramid Hedging (advanced, target-driven)",
        ],
        videoSrc: BYOS_VIDEO_SRC,
        script: PB_SCRIPT_DIR + "05_bring_your_own_strategy.md",
      },
      {
        id: "03", title: "Understanding the Rule Engine", duration: "2:39 min", priority: "P0",
        desc: "The conceptual foundation shared by every strategy — including yours: how facts, conditions, and actions work together to drive every automated decision the system makes.",
        topics: [
          "Facts: recalculated every 5-minute cycle from live positions and market data",
          "Conditions: AND/OR logic trees that evaluate facts against thresholds",
          "Actions: what the engine suggests — and what an action handler for your own rule would look like",
          "Groups, priority, and cooldowns: preventing queue flooding on rapid price moves",
        ],
        videoSrc: RULE_ENGINE_VIDEO_SRC,
        script: PB_SCRIPT_DIR + "07_understanding_the_rule_engine.md",
      },
    ],
  },

  /* ─────────────────────────────────────────────────────────────────────
   * 2 — PLATFORM FEATURES (promoted to a full top-level section 2026-09-04,
   * was previously nested inside Platform Basics). Every day-to-day
   * operational feature, one short clip each, so a viewer can scan the
   * whole set at a glance without one long video. NEW-BACKTEST, NEW-AIBOT,
   * NEW-ADMIN, and NEW-TICKETS have no existing footage anywhere and are
   * script-only for now — see their .md files in
   * tutorials/scripts/demo_showcase/platform_basics/.
   * ───────────────────────────────────────────────────────────────────── */
  {
    series: "Platform Features",
    color: "#545cd8",
    icon: "🧭",
    description: "Every day-to-day feature at a glance — short, focused clips so you can see exactly what you need without sitting through one long video.",
    items: [
      {
        id: "01", title: "Dashboard Walkthrough", duration: "90s", priority: "P0",
        desc: "A full tour of the main dashboard: P&L cards, strategy status, position summary, and how to navigate between the platform's core pages.",
        topics: [
          "Total P&L, stock P&L, option P&L, and potential P&L — what each card means",
          "Active strategies and active rules badges: what triggers a number change",
          "Quick-action buttons: jump directly to suggestions, rules, positions, or strategy builder",
        ],
        videoSrc: DASHBOARD_VIDEO_SRC,
        script: PB_SCRIPT_DIR + "06_dashboard_walkthrough.md",
      },
      {
        id: "NEW-POSITIONS", title: "Portfolio Positions", duration: "~2 min", priority: "P1",
        desc: "A single positions table for everything you hold — filter, sort, and pick from 17 optional columns to show exactly what matters to you, whatever strategy you're running.",
        topics: [
          "Live trading balance and P&L summary, straight from your broker",
          "17 optional columns, drag-to-reorder — turn on delta/hedge columns only if your strategy uses them",
          "Filter, sort, and place a manual trade without leaving the page",
        ],
        videoSrc: PORTFOLIO_POSITIONS_VIDEO_SRC,
        script: PB_SCRIPT_DIR + "12_portfolio_positions.md",
      },
      {
        id: "07", title: "The Automation Approval Flow", duration: "2 min", priority: "P0",
        desc: "How rule-generated suggestions become executed trades — the full flow from rule fire through suggestion card to filled order, including order queue and execution history.",
        topics: [
          "Rule fire → suggestion card: what triggers a card and what it shows",
          "Approving a suggestion: what happens next and how long execution takes",
          "Order queue: tracking pending, executing, and filled orders",
          "Execution history: the permanent audit trail for every decision",
        ],
        videoSrc: APPROVAL_FLOW_VIDEO_SRC,
      },
      {
        id: "08", title: "Setting Up Price Alerts", duration: "90s", priority: "P2",
        desc: "Configure Telegram and email price threshold alerts on any tracked symbol so you're notified the moment a stock hits a level you care about.",
        topics: [
          "Adding a price alert: symbol, direction (above/below), and threshold",
          "Telegram and email notification channels: how to connect and test each",
          "How alerts interact with the rule engine — alerts notify, rules act",
        ],
        videoSrc: PRICE_ALERTS_VIDEO_SRC,
      },
      {
        id: "08B", title: "Notification Settings", duration: "1:38 min", priority: "P1",
        desc: "Different from Price Alerts: this controls how the platform reaches you when the system itself does something — a new suggestion, an order fill, an order failure, or a critical error.",
        topics: [
          "Two channels: Email and Telegram, each with its own toggle and test button",
          "Telegram setup: message @userinfobot for your numeric Chat ID, then Send Test Message",
          "Why this is separate from Price Alerts — system events vs. price thresholds",
        ],
        videoSrc: NOTIFICATION_SETTINGS_VIDEO_SRC,
      },
      {
        id: "04", title: "Creating Rules from Templates", duration: "2 min", priority: "P1",
        desc: "The Rule Builder's template library — import pre-built rule bundles by category instead of writing every condition from scratch.",
        topics: [
          "Rule groups: Roll Management, Income-First Mode, Exit Options, Expiry Management",
          "Priority order: why some rules must rank higher than others",
          "The template browser, at a glance",
        ],
        videoSrc: RULE_TEMPLATES_VIDEO_SRC,
      },
      {
        id: "01B", title: "Connecting Your Brokerage", duration: "2:21 min", priority: "P0",
        desc: "A single OAuth connection covers 35+ brokerages — Alpaca, Schwab, Fidelity, E-Trade, Robinhood, and more — with no credential ever typed into the platform itself.",
        topics: [
          "Why a connected brokerage is required before any strategy can be built or activated",
          "One login covers 35+ brokerages via SnapTrade",
          "Reading connection status: Read-only vs. Trading Enabled",
        ],
        videoSrc: BROKERAGE_CONNECT_VIDEO_SRC,
      },
      {
        id: "09", title: "Configuring Your Account", duration: "2 min", priority: "P1",
        desc: "Global account settings that apply across all your strategies, not just one — thresholds, adjustment mode, and subscription plan limits.",
        topics: [
          "Global delta thresholds all strategies inherit",
          "Adjustment type: suggest vs. automatically adjust",
          "Subscription plan limits: max strategies, max accounts, cycle frequency",
        ],
        videoSrc: SYSTEM_SETTINGS_VIDEO_SRC,
      },
      {
        id: "NEW-BACKTEST", title: "Backtesting Overview", duration: "~90s", priority: "P1",
        desc: "See exactly how a rule set would have performed before you risk a dollar. Pick a symbol, a strategy, and a date range, then replay real historical data through the exact same rule engine that runs live.",
        topics: [
          "Auto-Run vs. Interactive step-by-step approval",
          "Reading the Adjustments Log and P&L view after a run",
          "The same mechanism behind every real backtest result shown elsewhere in this library",
        ],
        videoSrc: BACKTEST_OVERVIEW_VIDEO_SRC,
        script: PB_SCRIPT_DIR + "08_backtesting_overview.md",
      },
      {
        id: "NEW-AIBOT", title: "The AI Assistant", duration: "~90s", priority: "P1",
        desc: "A built-in AI assistant that understands the platform and your own account. Ask about a setting, a rule, or anything in your logs, and get an answer grounded in your real data — it explains, it doesn't trade for you.",
        topics: [
          "Ask about a setting, a rule, or an error in your logs — grounded in your real account state",
          "Session history sidebar to pick up a previous conversation",
          "Stays inside the same human-in-the-loop boundary as Trust and Control",
        ],
        videoSrc: AI_ASSISTANT_VIDEO_SRC,
        script: PB_SCRIPT_DIR + "09_ai_assistant.md",
      },
      {
        id: "NEW-ADMIN", title: "Admin & System Health", duration: "~2 min", priority: "P1",
        desc: "Manage every user and account under your portal in one place — subscription plans, live system health, and automated checks that flag problems before they become costly.",
        topics: [
          "User/account management: add, edit, assign plans, set status",
          "System Health: live vitals plus full automation-cycle history",
          "Audit: automated checks that surface problems for a human to review — never acts on its own",
        ],
        videoSrc: PLATFORM_BASICS_PLACEHOLDER,
        script: PB_SCRIPT_DIR + "10_admin_and_system_health.md",
      },
      {
        id: "NEW-TICKETS", title: "Support & Tickets", duration: "~44s", priority: "P2",
        desc: "A direct line to our support team whenever you need a human — simple and fast, with no queue to manage.",
        topics: [
          "Name, email, subject, message — submitted straight to the support inbox",
          "No queue or dashboard to manage — a direct line, not a tracking system",
        ],
        videoSrc: SUPPORT_TICKETS_VIDEO_SRC,
        script: PB_SCRIPT_DIR + "11_support_tickets.md",
      },
    ],
  },

  /* ─────────────────────────────────────────────────────────────────────
   * 3 — WEEKLY INDEX SHORT STRADDLE (showcase strategy #1 — reordered
   * 2026-09-04 to come before Pyramid Hedging: simpler to understand,
   * so it's the gentler on-ramp into the two strategy showcases)
   * ───────────────────────────────────────────────────────────────────── */
  {
    series: "Weekly Index Short Straddle — Capability Showcase",
    color: "#f59e0b",
    icon: "🎯",
    description: "A basic, delta-neutral hedge on a weekly index — simple to understand, genuinely hard to run by hand because it needs adjustments intraday.",
    items: [
      {
        // Rewritten 2026-09-05 for V3 (intraday-only) — the strategy no longer
        // holds positions across the week; see tutorials/scripts/wiss/wiss_intro.md
        // for the full correction (script was drifted against the old V2 design).
        id: "WISS-I", title: "Weekly Index Short Straddle: Introduction", duration: "2:16", priority: "P0",
        desc: "How Weekly Index Short Straddle works: a fresh at-the-money SPX straddle opened every trading day, re-centered intraday if SPX drifts, and force-closed before the close — no multi-day holding.",
        topics: [
          "SPX weekly options: $5 strike grid, European-style, cash-settled",
          "Daily open, intraday re-center on a strike cross, daily force-close",
          "No rolling, no recovery logic, no profit target — the design is deliberately this short",
        ],
        videoSrc: WISS_INTRO_VIDEO_SRC,
      },
      {
        // Rewritten 2026-09-05 for V3 — collapsed from 3 chapters to one video,
        // see tutorials/scripts/wiss/wiss_settings_guide.md: V3 has only 5
        // settings total, not enough surface area left to split into chapters.
        id: "WISS-S", title: "Weekly Index Short Straddle: Settings Guide", duration: "~2:30", priority: "P1",
        desc: "All five Weekly Index Short Straddle settings in one video — the daily trading window, lot size, and the low-premium safeguard.",
        topics: [
          "Open Time, Adjustment Cutoff Time, and End-of-Day Close Time — the daily trading window",
          "Lot size and the automatic $5-grid strike selection",
          "Minimum Leg Premium — the low-premium safeguard that rolls to next week's expiry",
        ],
        videoSrc: PLACEHOLDER,
      },
      {
        // Rewritten 2026-09-05 for V3 — no standard/recovery rolls or exit
        // rules anymore; see tutorials/scripts/wiss/wiss_settings_guide.md.
        id: "S02-WISS", title: "Importing Your WISS Starter Rules", duration: "~2 min", priority: "P1",
        desc: "The same starter-rules workflow as Pyramid Hedging's — daily open, intraday re-center, end-of-day close, and the low-premium safeguard, imported in one click.",
        topics: [
          "The WISS template category",
          "Why there's no ladder/accumulation rules — a single fresh position each day, not a built-up one",
        ],
        videoSrc: PLACEHOLDER,
      },
    ],
  },

  /* ─────────────────────────────────────────────────────────────────────
   * 4 — PYRAMID HEDGING (showcase strategy #2 — reframed, not a sales pitch;
   * reordered 2026-09-04 to come after WISS — see note above)
   * ───────────────────────────────────────────────────────────────────── */
  {
    series: "Pyramid Hedging — Capability Showcase",
    color: "#7c3aed",
    icon: "📈",
    description: "See exactly how it works — every setting, real backtest proof. This is what a fully engineered strategy looks like on this platform's rule engine.",
    children: [
      {
        series: "Strategy Guide",
        color: "#7c3aed",
        icon: "📘",
        description: "The core concept, the Strategy Builder UI, and every setting Pyramid Hedging supports.",
        items: [
          {
            id: "C00", title: "Pyramid Hedging: The Swiss-Army Strategy", duration: "2 min", priority: "P0",
            desc: "One engine, four configurations. See how a single rule-driven strategy adapts from a simple hedge to a full income-and-protection structure.",
            topics: [
              "Base configuration (ladder + single call hedge)",
              "+ Cash-Secured Put: two income engines from day one",
              "+ CSP + Income-First Mode: a profit-floor configuration",
              "What this level of configuration could look like, built around your own strategy",
            ],
            videoSrc: PH_SWISS_ARMY_VIDEO_SRC,
          },
          {
            id: "PH-S", title: "Pyramid Hedging: Settings Guide", duration: "~9 min", priority: "P1",
            desc: "A complete walkthrough of every Pyramid Hedging setting, organized into 4 chapters.",
            topics: [
              "Chapter 1 — The Ladder: step gap, budget cap, effective average cost",
              "Chapter 2 — Hedge Style: bullish vs income-focused call placement",
              "Chapter 3 — Income-First Mode: a configurable minimum-profit floor",
              "Chapter 4 — The CSP: safety buffer, rollup trigger, income contribution",
            ],
            videoSrc: PH_SETTINGS_GUIDE_VIDEO_SRC,
          },
        ],
      },
      {
        series: "Trading Setups — real rule sets in action",
        color: "#7c3aed",
        icon: "🧩",
        description: "Two real example configurations — without the Cash-Secured Put leg, and with it — each shown with real account numbers and real backtest results.",
        children: [
          {
            series: "Setup 1: Cash-Secured Put Disabled",
            color: "#7c3aed",
            icon: "1️⃣",
            description: "Stock and a single call hedge, no put leg — the simpler two-leg structure.",
            items: [
              {
                id: "TE01", title: "Setup 1 — Concept: The Call-Only Structure", duration: "~2 min", priority: "P1",
                desc: "A real NVDA account showing the two-leg, call-only structure in action.",
                topics: [
                  "Call-only structure: two legs, no CSP buying-power reserved",
                  "Real NVDA numbers from a live example account",
                  "When call-only is one configuration choice among several",
                ],
                videoSrc: PH_TE01_VIDEO_SRC,
              },
              {
                id: "TE03", title: "Setup 1 — Income-First Mode in Action: OFF", duration: "~2 min", priority: "P1",
                desc: "Same NVDA account with Income-First Mode switched off — a real reference point for how the toggle changes ladder behavior (Income-First Mode is on by default).",
                topics: [
                  "Hedge Style alone governs gap width in this mode, no profit target doing any work",
                  "Minimum Ladder Step Gap is a floor, not the answer",
                  "Real NVDA numbers: ~1.5% gap, $222.50 call strike",
                ],
                videoSrc: PH_TE03_VIDEO_SRC,
              },
              {
                id: "TE04", title: "Setup 1 — Income-First Mode in Action: ON (The Default)", duration: "~2 min", priority: "P1",
                desc: "Same NVDA account, one toggle flipped — see exactly what Income-First Mode does on its own.",
                topics: [
                  "Minimum Cycle Profit Target: the real number this mode is built to defend",
                  "Selection order: fewest lots wins first, then the widest gap that still protects the floor",
                  "Real numbers: $230 call strike, gap narrows to ~1.25%, Tail P&L $1,022",
                ],
                videoSrc: PH_TE04_VIDEO_SRC,
              },
              {
                id: "BT02", title: "Setup 1 — Backtest: Falling Market (MSFT)", duration: "~4 min", priority: "P1",
                desc: "A real one-month backtest showing how the hedge defends itself through a sharp market decline.",
                topics: [
                  "Real historical MSFT decline, real Adjustments Log",
                  "ROLL_ON_EBP_BREACH firing before the crash",
                  "End-of-month summary from real backtest output",
                ],
                videoSrc: PH_BT02_VIDEO_SRC,
              },
            ],
          },
          {
            series: "Setup 2: Cash-Secured Put Enabled",
            color: "#7c3aed",
            icon: "2️⃣",
            description: "Adds a cash-secured put to the same ladder and call hedge — two income engines instead of one.",
            items: [
              {
                id: "TE02", title: "Setup 2 — Concept & Settings: Cash-Secured Put Enabled", duration: "~2 min", priority: "P1",
                desc: "Same NVDA account, same call — flip on the Cash-Secured Put and total profit at completion more than doubles. See exactly where the extra income comes from.",
                topics: [
                  "CSP Safety Buffer and CSP Rollup Trigger Distance settings explained with real values",
                  "Real before/after: Tail P&L $198.92 (call only) → $453.79 (call + CSP)",
                  "Buying-power check: strike × 100 × lots reserved as collateral before enabling",
                ],
                videoSrc: PH_TE02_VIDEO_SRC,
              },
              {
                id: "C06", title: "Setup 2 — Deep Dive: Configuration Depth With Both Legs On", duration: "4 min", priority: "P1",
                desc: "A deep dive into the mechanics: how the ladder is sized to guarantee a profit floor, and how automated delta-hedge rolls defend against losses — including the toughest case, where the CSP goes in-the-money.",
                topics: [
                  "Tail P&L: the one number that drives ladder size",
                  "How Income-First Mode searches for the smallest ladder that clears the Minimum Cycle Profit Target",
                  "delta_difference and ROLL_DOWN_CALL: automated delta-neutral rebalancing every 5-minute cycle",
                  "The ITM-put rescue: rolling the CSP toward ATM to restore the profit floor",
                ],
                videoSrc: PH_C06_VIDEO_SRC,
              },
            ],
          },
        ],
      },
      {
        series: "Get Started",
        color: "#f7b924",
        icon: "⚙️",
        description: "The exact workflow you'd use for your own rule set once onboarded: import a starter set, then prove it against real historical data.",
        items: [
          {
            id: "S02", title: "Importing a Starter Rule Set", duration: "~2 min", priority: "P0",
            desc: "The rule template browser and one-click import — same mechanic regardless of whose strategy the rules encode.",
            topics: [
              "The template browser: categories and per-rule descriptions",
              "Importing a starting bundle in one click",
              "Confirming rules are ACTIVE in the Rule Builder after import",
            ],
            videoSrc: PLACEHOLDER,
          },
          {
            id: "S03", title: "Backtesting Your Rules", duration: "~2 min", priority: "P0",
            desc: "The Backtest page against real historical data — proof the engine executes exactly what your rules say, before any live capital is at risk.",
            topics: [
              "Picking a symbol and date range",
              "Reading the Adjustments Log: every simulated trade and the rule that triggered it",
              "The P&L view: telling if a rule set behaves as expected",
            ],
            videoSrc: PLACEHOLDER,
          },
        ],
      },
    ],
  },

  /* ─────────────────────────────────────────────────────────────────────
   * 5 — GET THIS BUILT FOR YOU (new — the actual conversion point)
   * ───────────────────────────────────────────────────────────────────── */
  {
    series: "Get This Built For You",
    color: "#16a34a",
    icon: "🛠️",
    description: "You've seen what the engine can do. Now build something that's entirely yours.",
    items: [
      {
        id: "NEW-CUSTOM-CTA", title: "Want This Built Around Your Own Strategy?", duration: "~45s", priority: "P0",
        desc: "Pyramid Hedging and Weekly Index Short Straddle show you what's possible. Your own strategy — built from your own rules — runs on the exact same engine, with the exact same approval gate.",
        topics: [
          "A fully bespoke build, engineered from your own rules — not a modified version of PH or WISS",
          "Runs on the same engine and the same approval gate you've seen throughout this library",
          "One-time setup fee scaled to your strategy's complexity, plus a flat monthly platform and support fee",
          "Get started with a conversation about your strategy",
        ],
        videoSrc: PLACEHOLDER,
      },
    ],
  },
];

/* ── generic node helpers (works at any depth) ────────────────────────────── */
function nodeItems(node) {
  if (node.items) return node.items;
  if (node.children) return node.children.flatMap(nodeItems);
  return [];
}

function openPathForId(id) {
  if (!id) return [];
  function walk(nodes, path) {
    for (const node of nodes) {
      const p = [...path, node.series];
      if (node.items) {
        if (node.items.some(i => i.id === id)) return p;
      } else if (node.children) {
        const found = walk(node.children, p);
        if (found) return found;
      }
    }
    return null;
  }
  return walk(SHOWCASE_TUTORIALS, []) || [];
}

function pathKey(path) {
  return JSON.stringify(path);
}

function openKeysForId(id) {
  const path = openPathForId(id);
  const keys = [];
  for (let i = 1; i <= path.length; i++) keys.push(pathKey(path.slice(0, i)));
  return keys;
}

function SectionHeader({ node, index, isOpen, onToggle, depth }) {
  const items = nodeItems(node);
  const nested = depth > 0;
  return (
    <div
      onClick={onToggle}
      role="button"
      tabIndex={0}
      onKeyDown={e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onToggle(); } }}
      style={{
        display: "flex", alignItems: "flex-start", gap: nested ? 10 : 12,
        marginBottom: isOpen ? (nested ? 12 : 16) : 0, paddingBottom: nested ? 8 : 12,
        borderBottom: `2px solid ${node.color}33`,
        cursor: "pointer", userSelect: "none",
      }}
    >
      <div style={{
        width: nested ? 28 : 36, height: nested ? 28 : 36, borderRadius: nested ? 8 : 10, flexShrink: 0,
        background: `linear-gradient(135deg, ${node.color}22, ${node.color}44)`,
        border: `1px solid ${node.color}33`,
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: nested ? 13 : 18,
      }}>
        {node.icon}
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: node.description ? 4 : 0, flexWrap: "wrap" }}>
          <span style={{
            fontSize: nested ? 10 : 12, color: "#999", flexShrink: 0,
            display: "inline-block", transition: "transform 0.15s",
            transform: isOpen ? "rotate(90deg)" : "rotate(0deg)",
          }}>
            ▶
          </span>
          <span style={{ fontSize: depth === 0 ? 15 : depth === 1 ? 13.5 : 12.5, fontWeight: 700, color: "#222" }}>
            {index != null ? `${index}. ` : ""}{node.series}
          </span>
          <span style={{
            fontSize: 11, color: node.color, fontWeight: 600,
            background: `${node.color}15`, borderRadius: 20, padding: "1px 8px",
            border: `1px solid ${node.color}33`,
          }}>
            {items.length} video{items.length !== 1 ? "s" : ""}
          </span>
        </div>
        {node.description && (
          <p style={{ margin: 0, fontSize: nested ? 11.5 : 12, color: "#777", lineHeight: 1.5 }}>
            {node.description}
          </p>
        )}
      </div>
    </div>
  );
}

function TutorialCard({ item, node, highlightId, highlightRef, onPlay }) {
  const isHighlighted = item.id === highlightId;
  const isReady = Boolean(item.videoSrc);
  return (
    <div
      key={item.id}
      ref={isHighlighted ? highlightRef : null}
      style={{
        display: "flex", alignItems: "flex-start", gap: 14,
        background: isHighlighted ? `${node.color}0d` : "#fff",
        border: `1px solid ${isHighlighted ? node.color : "#eee"}`,
        borderLeft: `4px solid ${isHighlighted ? node.color : isReady ? node.color + "66" : "#ddd"}`,
        borderRadius: 10,
        padding: "12px 16px",
        opacity: isReady ? 1 : 0.78,
        transition: "border-color 0.2s, background 0.2s, opacity 0.2s",
        boxShadow: isHighlighted ? `0 2px 12px ${node.color}22` : "0 1px 3px rgba(0,0,0,0.04)",
      }}
      onMouseEnter={e => {
        if (!isHighlighted && isReady) {
          e.currentTarget.style.borderColor = `${node.color}66`;
          e.currentTarget.style.background = "#fafafa";
        }
      }}
      onMouseLeave={e => {
        if (!isHighlighted) {
          e.currentTarget.style.borderColor = isReady ? `${node.color}66` : "#ddd";
          e.currentTarget.style.background = "#fff";
        }
      }}
    >
      <div style={{
        width: 56, height: 44, borderRadius: 8, flexShrink: 0,
        background: isReady
          ? `linear-gradient(135deg, ${node.color}22 0%, ${node.color}44 100%)`
          : "linear-gradient(135deg, #f0f0f0, #e0e0e0)",
        border: `1px solid ${isReady ? node.color + "33" : "#ddd"}`,
        display: "flex", alignItems: "center", justifyContent: "center",
        position: "relative", overflow: "hidden",
        fontSize: 18,
      }}>
        {isReady ? node.icon : "🔜"}
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3, flexWrap: "wrap" }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: isReady ? "#222" : "#666" }}>
            {item.title}
          </span>
          {isHighlighted && (
            <span style={{
              fontSize: 9, background: node.color, color: "#fff",
              borderRadius: 10, padding: "1px 6px", fontWeight: 700,
            }}>
              SUGGESTED
            </span>
          )}
        </div>
        <div style={{
          fontSize: 12, color: "#777", lineHeight: 1.45, marginBottom: item.topics?.length ? 6 : 0,
        }}>
          {item.desc}
        </div>
        {item.topics?.length > 0 && (
          <ul style={{ margin: "4px 0 0", paddingLeft: 16 }}>
            {item.topics.map((t, i) => (
              <li key={i} style={{ fontSize: 11, color: "#888", marginBottom: 2, lineHeight: 1.45 }}>
                {t}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6, flexShrink: 0 }}>
        <div style={{ display: "flex", gap: 4 }}>
          <span style={{
            fontSize: 10, background: `${node.color}15`, color: node.color,
            border: `1px solid ${node.color}33`,
            borderRadius: 20, padding: "1px 7px", fontWeight: 600,
          }}>
            {item.duration}
          </span>
          {item.priority === "P0" && (
            <span style={{
              fontSize: 10, background: "#fff3cd", color: "#856404",
              border: "1px solid #ffc107", borderRadius: 20, padding: "1px 7px", fontWeight: 600,
            }}>
              Essential
            </span>
          )}
        </div>
        <button
          onClick={() => onPlay({ ...item, series: node.series })}
          style={{
            background: node.color,
            color: "#fff",
            border: "none", borderRadius: 6,
            fontSize: 11, fontWeight: 600,
            padding: "5px 14px",
            cursor: "pointer",
            whiteSpace: "nowrap",
          }}
          onMouseEnter={e => { e.currentTarget.style.opacity = "0.85"; }}
          onMouseLeave={e => { e.currentTarget.style.opacity = "1"; }}
        >
          ▶ Watch
        </button>
      </div>
    </div>
  );
}

function TreeNode({ node, index, depth, parentPath, openSections, toggleSection, highlightId, highlightRef, onPlay }) {
  const path = [...parentPath, node.series];
  const key = pathKey(path);
  const isOpen = openSections.has(key);
  return (
    <div style={{
      marginBottom: isOpen ? (depth === 0 ? 40 : 20) : (depth === 0 ? 12 : 4),
      marginLeft: depth > 0 ? 20 : 0,
    }}>
      <SectionHeader
        node={node}
        index={index}
        isOpen={isOpen}
        onToggle={() => toggleSection(key)}
        depth={depth}
      />

      {isOpen && node.children && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {node.children.map(child => (
            <TreeNode
              key={child.series}
              node={child}
              index={null}
              depth={depth + 1}
              parentPath={path}
              openSections={openSections}
              toggleSection={toggleSection}
              highlightId={highlightId}
              highlightRef={highlightRef}
              onPlay={onPlay}
            />
          ))}
        </div>
      )}

      {isOpen && node.items && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: depth > 0 ? 12 : 0 }}>
          {node.items.map(item => (
            <TutorialCard
              key={item.id}
              item={item}
              node={node}
              highlightId={highlightId}
              highlightRef={highlightRef}
              onPlay={onPlay}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function DemoShowcaseTree({ highlightId, onPlay }) {
  const highlightRef = useRef(null);

  const [openSections, setOpenSections] = useState(() => new Set(openKeysForId(highlightId)));

  useEffect(() => {
    const keys = openKeysForId(highlightId);
    if (!keys.length) return;
    setOpenSections(prev => {
      const next = new Set(prev);
      let changed = false;
      for (const k of keys) if (!next.has(k)) { next.add(k); changed = true; }
      return changed ? next : prev;
    });
  }, [highlightId]);

  useEffect(() => {
    if (highlightRef.current) {
      setTimeout(() => highlightRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 120);
    }
  }, [highlightId]);

  const toggleSection = (key) => {
    setOpenSections(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const allItems = nodeItems({ children: SHOWCASE_TUTORIALS });
  const totalVideos = allItems.length;

  return (
    <div style={{ padding: "24px 28px", maxWidth: 900, margin: "0 auto" }}>

      <div style={{ marginBottom: 24 }}>
        <h2 style={{ margin: "0 0 6px", fontSize: 22, fontWeight: 800, color: "#1a1a2e" }}>
          Video Library
        </h2>
        <p style={{ margin: "0 0 10px", fontSize: 13, color: "#888" }}>
          {totalVideos} videos · See exactly how the platform works — from the core rule engine and every day-to-day feature, to two fully engineered strategies shown end to end.
        </p>
      </div>

      {SHOWCASE_TUTORIALS.map((node, idx) => (
        <TreeNode
          key={node.series}
          node={node}
          index={idx + 1}
          depth={0}
          parentPath={[]}
          openSections={openSections}
          toggleSection={toggleSection}
          highlightId={highlightId}
          highlightRef={highlightRef}
          onPlay={onPlay}
        />
      ))}
    </div>
  );
}
